import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../environments/environment';

const CHECKOUT_SIGNIN_EMAIL_TTL_MS = 5 * 60 * 1000;
const CHECKOUT_SIGNIN_EMAIL_COOKIE = 'sync_checkout_email';
const AUTH_SESSION_COOKIE = 'sync_auth';

@Injectable({
  providedIn: 'root',
})
export class SyncCookieService {
  constructor(private cookies: CookieService) {}

  public setViewedLink(): void {
    const expDate = new Date();
    expDate.setMonth(expDate.getMonth() + 3);
    this.cookies.set('sync_link', '1', expDate, '/', environment.cookiedomain, environment.cookiesecure, 'Lax');
  }

  public setLinkPassword(linkId: string, linkPassword: string): void {
    const expDate = Date.now() + 600000; // 10 minutes
    this.cookies.set(
      `linkpassword-${linkId}`,
      linkPassword,
      new Date(expDate),
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }
  public getLinkPassword(linkId: string): string {
    return this.cookies.get(`linkpassword-${linkId}`);
  }
  public deleteLinkPassword(linkId: string): void {
    return this.cookies.delete(`linkpassword-${linkId}`, '/');
  }
  public setOrderSessionId(sessionId: string): void {
    const expDate = Date.now() + 10800 * 1000; // 3 hours
    this.cookies.set(
      `sync-order-session-id`,
      sessionId,
      new Date(expDate),
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }
  public setOrderSessionHashData(saltData: string): void {
    const expDate = Date.now() + 10800 * 1000; // 3 hours
    this.cookies.set(
      `sync-order-session-hash-data`,
      saltData,
      new Date(expDate),
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }
  public setOrderSessionSaltData(saltData: string): void {
    const expDate = Date.now() + 10800 * 1000; // 3 hours
    this.cookies.set(
      `sync-order-session-salt-data`,
      saltData,
      new Date(expDate),
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }
  public setDownloadPubLink(passwordlock: string): void {
    this.cookies.set(
      'passwordlock',
      encodeURIComponent(passwordlock),
      undefined,
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }

  public setDownloadPubUser(signature: string): void {
    this.cookies.set('signature', signature, undefined, '/', environment.cookiedomain, environment.cookiesecure, 'Lax');
  }

  public setThumbnailSignature(signature: string): void {
    this.cookies.set(
      'thumbnail_sign',
      signature,
      undefined,
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }

  public setDisableAnalytics(disabled: string): void {
    this.cookies.set(
      'is_disableanalytics',
      disabled,
      undefined,
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }

  /** Carries the session to consumers that don't read the store. */
  public setAuthSession(token: string): void {
    const expDate = new Date();
    expDate.setMonth(expDate.getMonth() + 3);
    this.cookies.set(
      AUTH_SESSION_COOKIE,
      token,
      expDate,
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }

  public getAuthSession(): string {
    return this.cookies.get(AUTH_SESSION_COOKIE);
  }

  public deleteAuthSession(): void {
    this.deleteCookie(AUTH_SESSION_COOKIE);
  }

  public deleteCookie(name: string): void {
    this.cookies.delete(name, '/', environment.cookiedomain);
  }

  public getMarketingRef(): string {
    return this.cookies.get('_sync_mktg');
  }

  public getHubspotUTK(): string {
    return this.cookies.get('hubspotutk');
  }
  public getUtmParams(): string {
    return this.cookies.get('utm_params');
  }
  public getUtmParamsSource(): string {
    return this.cookies.get('utm_params_source');
  }
  public getUtmParamsMedium(): string {
    return this.cookies.get('utm_params_medium');
  }
  public getUtmParamsKeyword(): string {
    return this.cookies.get('utm_params_keyword');
  }
  public getUtmParamsGclid(): string {
    return this.cookies.get('utm_params_gclid');
  }
  public getPageUri(): string {
    return this.cookies.get('page_uri');
  }
  public getHubspotCookieConsent(): string {
    return this.cookies.get('__hs_cookie_cat_pref');
  }
  public getPageName(): string {
    return this.cookies.get('page_name');
  }
  public getPageId(): string {
    return this.cookies.get('page_id');
  }

  /**
   * From the `ph_<projectKey>_posthog` cookie the marketing site writes on the shared parent
   * domain. cp holds no PostHog project key, so the cookie is matched by shape, not by name.
   */
  public getPostHogDistinctId(): string {
    const match = Object.entries(this.cookies.getAll() ?? {}).find(([name]) => /^ph_.+_posthog$/.test(name));
    if (!match) {
      return '';
    }
    try {
      return (JSON.parse(match[1]) as { distinct_id?: string })?.distinct_id ?? '';
    } catch {
      return '';
    }
  }

  public getAnonymousId(): string {
    return this.cookies.get('sync_anon_id');
  }

  public setAnonymousId(id: string): void {
    const expDate = new Date();
    expDate.setFullYear(expDate.getFullYear() + 1);
    this.cookies.set('sync_anon_id', id, expDate, '/', environment.cookiedomain, environment.cookiesecure, 'Lax');
  }

  /** A cookie, not a redirect query param, which would log the address on the destination host. */
  public setCheckoutSigninEmail(email: string): void {
    const expDate = new Date(Date.now() + CHECKOUT_SIGNIN_EMAIL_TTL_MS);
    this.cookies.set(
      CHECKOUT_SIGNIN_EMAIL_COOKIE,
      email,
      expDate,
      '/',
      environment.cookiedomain,
      environment.cookiesecure,
      'Lax',
    );
  }

  public consumeCheckoutSigninEmail(): string {
    const email = this.cookies.get(CHECKOUT_SIGNIN_EMAIL_COOKIE);
    if (email) {
      this.deleteCookie(CHECKOUT_SIGNIN_EMAIL_COOKIE);
    }
    return email;
  }
}
